import React from 'react'
import { AvatarGroup, Box, Stack, Typography } from '@mui/material'
import UserInline from '@app/src/components/Ui/UserInline'
import PersonAvatar from '@app/src/pages/ResourceCollection/Collections/CellComponents/PersonAvatar'
import { AvatarSize } from './Avatar'
import type { User } from '@app/src/types/resourceExplorer'
import { PersonOffOutlined } from '@mui/icons-material'
import { useIntl } from 'react-intl'

export type UserGroupProps = {
  users: ReadonlyArray<User> | undefined
  max?: number
  label?: string
  onClick?: () => void
  avatarPx?: number
  gap?: number
  typographyVariant?: 'caption' | 'body2' | 'body1'
}

const UserGroup: React.FC<UserGroupProps> = ({
  users,
  max = 3,
  label,
  onClick,
  avatarPx = 24,
  gap = 0.5,
  typographyVariant = 'caption',
}) => {
  const { formatMessage } = useIntl()

  if (!users || users.length === 0) {
    return (
      <Box display="flex" gap={1}>
        <PersonOffOutlined color="disabled" />
        <Typography color="textSecondary">{formatMessage({ id: 'resourceExplorer.provider.notAdded' })}</Typography>
      </Box>
    )
  }

  if (users.length === 1) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        gap={gap}
        sx={{ '&:hover': { cursor: onClick ? 'pointer' : 'default' } }}
      >
        <UserInline name={users[0].name ?? ''} onClick={onClick} />
      </Stack>
    )
  }

  const computedFontSize = Math.max(10, Math.round(avatarPx / 2))

  const handleClick = () => {
    if (onClick) {
      onClick()
    }
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={gap}
      onClick={handleClick}
      sx={{ '&:hover': { cursor: onClick ? 'pointer' : 'default' } }}
    >
      <AvatarGroup
        max={max}
        sx={{
          '& .MuiAvatar-root': {
            width: avatarPx,
            height: avatarPx,
            fontSize: computedFontSize,
          },
        }}
      >
        {users.map(user => (
          <PersonAvatar key={user.id} name={user.name ?? ''} size={AvatarSize.Small} />
        ))}
      </AvatarGroup>
      <Typography variant={typographyVariant}>{label}</Typography>
    </Stack>
  )
}

export default UserGroup
